import clsx from "clsx";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NAV_ICONS } from "../icons/navIcons";
import { TruncatedText } from "../common/TruncatedText";

/**
 * The listing each detail page belongs to: [route prefix, listing path, its
 * label in the navigation namespace]. First match wins, so the specific
 * prefixes sit above the general ones. A detail page under a new route gets
 * its "← Listing" link by adding a line here.
 */
const LISTINGS: [prefix: string, path: string, labelKey: string][] = [
  ["/party/", "/party", "registeredParties"],
  ["/admin/staff/", "/admin/staffs", "staffs"],
  ["/admin/licensing/organisations/", "/admin/licensing", "licenseManagement"],
  ["/admin/organization/", "/admin/organization", "organisationSettings"],
  ["/admin/businessUnits/", "/admin/businessUnit", "businessUnit"],
  ["/admin/department/", "/admin/department", "department"],
  ["/contracts/legacy/", "/contracts/legacy-list", "legacyContracts"],
  ["/contracts/", "/contracts", "allContracts"],
  [
    "/library/contract-templates/",
    "/library/contract-templates/all",
    "contractTemplates",
  ],
  [
    "/library/clause-templates/",
    "/library/clause-templates/all",
    "clauseTemplates",
  ],
  ["/obligations-hub/", "/obligations-hub/all", "allObligations"],
  [
    "/receivables-engine/templates/email/",
    "/receivables-engine/templates/email",
    "emailTemplates",
  ],
  [
    "/receivables-engine/templates/notice/",
    "/receivables-engine/templates/notice",
    "noticeTemplates",
  ],
  ["/receivables-engine/", "/receivables-engine/all", "allReceivables"],
];

export function DetailLayout({
  title,
  subtitle,
  status,
  actions,
  children,
  backFallback = "/",
}: {
  title: React.ReactNode;
  subtitle?: string;
  status?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  backFallback?: string;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation("navigation");
  const BackIcon = NAV_ICONS.collapse;
  const listing = LISTINGS.find(([prefix]) => pathname.startsWith(prefix));

  return (
    <section className="p-3 md:p-4 space-y-4">
      {/* BACK TO LISTING — always the listing itself, unlike the arrow in the
          header, which steps back through history (and so can land on
          another record, a quick view, or wherever the link came from). */}
      {listing && (
        <Link
          to={listing[1]}
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-[rgb(var(--text-muted))] transition-colors hover:text-[rgb(var(--text-accent))]"
        >
          <ArrowLeft
            size={15}
            className="transition-transform group-hover:-translate-x-0.5"
          />
          {t(listing[2])}
        </Link>
      )}

      {/* HEADER */}
      <div
        className={clsx(
          "sticky top-0 z-30 backdrop-blur",
          "flex flex-col md:flex-row md:items-center md:justify-between gap-4",
          // Opacity goes on the modifier, not inside the brackets:
          // "bg-[rgb(var(--card))/0.9]" is not a value Tailwind can
          // parse, so the class was dropped and this sticky header
          // rendered with no background — the cards scrolled
          // straight through it.
          "bg-[rgb(var(--card))]/90",
          "border border-[rgb(var(--border))]",
          "rounded-xl p-4",
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          {/* GENERIC BACK */}
          <button
            title={t("common:back")}
            aria-label={t("common:back")}
            onClick={() =>
              window.history.length > 1 ? navigate(-1) : navigate(backFallback)
            }
            className="
              p-2 rounded-lg
              bg-[rgb(var(--card-muted))]
              hover:bg-[rgb(var(--card-hover))]
              border border-[rgb(var(--border))]
              text-[rgb(var(--text-primary))]
              transition
            "
          >
            <BackIcon size={16} />
          </button>

          {/* min-w-0 so a long record title clips here rather than pushing
              the status chip and the action menu off the header. */}
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-[rgb(var(--text-primary))]">
              {typeof title === "string" ? (
                <TruncatedText text={title} />
              ) : (
                title
              )}
            </h1>

            {subtitle && (
              <p className="text-sm text-[rgb(var(--text-muted))]">
                <TruncatedText text={subtitle} />
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {status}

          {actions && <div className="relative">{actions}</div>}
        </div>
      </div>

      {children}
    </section>
  );
}
